import { Injectable } from '@angular/core';

export interface CartItem {
  type: 'growth_subscription' | 'ultimate_subscription' | 'credits_100' | 'credits_250' | 'credits_750';
  id: string;           // unique plan ID like 'growth_plan'
  quantity: number;
}

@Injectable({ providedIn: 'root' })
export class CartService {
  private cart: CartItem[] = [];

  // New method to check if the cart has any subscription
  hasGrowthSubscription(): boolean {
    return this.cart.some(item => item.type === 'growth_subscription' && item.quantity > 0);
  }
  hasUltimateSubscription(): boolean {
    return this.cart.some(item => item.type === 'ultimate_subscription' && item.quantity > 0);
  }
  getItems(): CartItem[] {
    return [...this.cart];
  }

  addItem(item: CartItem): void {
    const isGrowthSubscription = item.type === 'growth_subscription';
    const isUltimateSubscription = item.type === 'ultimate_subscription';
    const isCredits100 = item.type === 'credits_100';
    const isCredits250 = item.type === 'credits_250';
    const isCredits750 = item.type === 'credits_750';

    if (isUltimateSubscription || isGrowthSubscription) {
      // Clear the entire cart and only keep this subscription
      this.cart = [{
        ...item,
        quantity: 1  // Subscriptions always have quantity 1
      }];
      return;
    }

    if (isCredits100 || isCredits250 || isCredits750) {
      // Remove all non-credit items
      if (item.type === 'credits_100') {
        this.cart = this.cart.filter(i => i.type === 'credits_100');
      }
      if (item.type === 'credits_250') {
        this.cart = this.cart.filter(i => i.type === 'credits_250');
      }
      if (item.type === 'credits_750') {
        this.cart = this.cart.filter(i => i.type === 'credits_750');
      }
      const existing = this.cart.find(i => i.id === item.id);

      if (existing) {
        existing.quantity += item.quantity;
      } else {
        this.cart.push({ ...item });
      }
    }
  }

  clear(): void {
    this.cart = [];
  }

  // Add one unit of a given item (by id and type)
  addOne(type: CartItem['type'], id: string): void {
    const existing = this.cart.find(i => i.id === id && i.type === type);

    // For subscriptions, max quantity is 1 (if it's growth_plan or ultimate_plan)
    const isSubscriptionWithLimit = (id === 'growth_plan' || id === 'ultimate_plan');

    if (existing) {
      if (isSubscriptionWithLimit) {
        // Do not increase quantity beyond 1
        if (existing.quantity < 1) {
          existing.quantity = 1;
        }
        // else do nothing, already at max 1
      } else {
        // No limit for other items (e.g. credits)
        existing.quantity += 1;
      }
    } else {
      // Add new item: for limited subscription max is 1, else quantity=1
      this.cart.push({ type, id, quantity: isSubscriptionWithLimit ? 1 : 1 });
    }
  }

  // Remove one unit of a given item (by id and type)
  removeOne(type: CartItem['type'], id: string): void {
    const existingIndex = this.cart.findIndex(i => i.id === id && i.type === type);
    if (existingIndex > -1) {
      const existing = this.cart[existingIndex];
      existing.quantity -= 1;
      if (existing.quantity <= 0) {
        this.cart.splice(existingIndex, 1);
      }
    }
  }

  isEmpty(): boolean {
    return this.cart.length === 0 || this.cart.every(item => item.quantity <= 0);
  }
}
